import { useState } from "react";
import { Link } from "react-router-dom";
import { api, errText, photoUrl } from "../../lib/api";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../ui/hover-card";
import { toast } from "sonner";
import { Clock, MessageCircle, PlayCircle, PauseCircle, BadgeCheck, BadgeX, Loader2, Star, Flag, ExternalLink } from "lucide-react";
import { TRUST_CLS, timeAgo } from "./shared";

export function UserPeek({ u, children, onMessage, onSuspend, onReactivate, onUnverify, canAct, isAdmin, onTrustChanged }) {
  const [trust, setTrust] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [exactVal, setExactVal] = useState("");
  const year = u.created_at ? new Date(u.created_at).getFullYear() : "";
  const status = u.status || "active";
  const adjustTrust = async (delta) => {
    setAdjusting(true);
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/trust-adjust`, { delta });
      setTrust(data);
      onTrustChanged?.();
    } catch (e) { toast.error(errText(e)); }
    finally { setAdjusting(false); }
  };
  const setExact = async () => {
    const t = parseInt(exactVal, 10);
    if (isNaN(t) || t < 0 || t > 100) { toast.error("Enter a score between 0 and 100"); return; }
    setAdjusting(true);
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/trust-adjust`, { target: t });
      setTrust(data);
      setExactVal("");
      onTrustChanged?.();
    } catch (e) { toast.error(errText(e)); }
    finally { setAdjusting(false); }
  };
  const onOpen = (open) => {
    if (open && !trust && !loading) {
      setLoading(true);
      api.get(`/admin/users/${u.user_id}/trust`).then((r) => setTrust(r.data)).catch(() => {}).finally(() => setLoading(false));
    }
  };
  return (
    <HoverCard openDelay={250} closeDelay={100} onOpenChange={onOpen}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent align="start" className="w-72 p-0 overflow-hidden" data-testid={`user-peek-${u.user_id}`}>
        <div className="bg-[#12234A] text-white p-3 flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-full bg-white/15 flex items-center justify-center overflow-hidden shrink-0">
            {u.picture ? <img src={photoUrl(u.picture)} alt="" className="h-full w-full object-cover" /> : <span className="text-sm font-bold">{u.name?.[0]}</span>}
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate flex items-center gap-1">{u.name}{u.is_verified_seller && <BadgeCheck size={13} className="text-emerald-400" />}</p>
            <p className="text-[11px] text-slate-300 truncate">{u.username ? `@${u.username} · ` : ""}{u.seller_type}{year ? ` · since ${year}` : ""}</p>
          </div>
        </div>
        <div className="p-3 space-y-2.5">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-slate-400 py-2"><Loader2 size={14} className="animate-spin" /> Loading stats…</div>
          ) : trust ? (
            <>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Trust score</span>
                <span data-testid={`user-peek-trust-${u.user_id}`} className={`text-[11px] font-bold rounded-full px-2 py-0.5 ${TRUST_CLS[trust.color] || TRUST_CLS.amber}`}>{trust.score}/100 · {trust.label}</span>
              </div>
              {isAdmin && (
                <div className="space-y-1.5 -mt-1" data-testid={`trust-adjust-${u.user_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] text-slate-400">{trust.adjustment ? `Manual: ${trust.adjustment > 0 ? "+" : ""}${trust.adjustment}` : "Adjust score"}</span>
                    <div className="flex items-center gap-1">
                      {[-5, -1, 1, 5].map((d) => (
                        <button key={d} disabled={adjusting} onClick={() => adjustTrust(d)} data-testid={`trust-adjust-${d > 0 ? "plus" : "minus"}${Math.abs(d)}-${u.user_id}`}
                          className={`h-6 min-w-[30px] px-1.5 rounded-md text-[11px] font-bold transition-colors disabled:opacity-40 ${d < 0 ? "bg-red-50 text-red-600 hover:bg-red-100" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}>
                          {d > 0 ? `+${d}` : d}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <input type="number" min="0" max="100" value={exactVal} onChange={(e) => setExactVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && setExact()} placeholder="Set exact score (0–100)" data-testid={`trust-set-input-${u.user_id}`} className="h-6 flex-1 rounded-md border border-slate-200 px-2 text-[11px] focus:outline-none focus:ring-1 focus:ring-[#12234A]" />
                    <button onClick={setExact} disabled={adjusting || exactVal === ""} data-testid={`trust-set-btn-${u.user_id}`} className="h-6 px-2.5 rounded-md text-[11px] font-bold bg-[#12234A] text-white hover:bg-[#0c1832] transition-colors disabled:opacity-40">Set</button>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg bg-slate-50 p-2">
                  <p className="text-slate-400 flex items-center gap-1 text-[10px]"><Star size={11} /> Reviews</p>
                  <p className="font-semibold text-[#12234A]">{trust.reviews.count ? `${trust.reviews.avg}★ · ${trust.reviews.count}` : "None"}</p>
                </div>
                <div className="rounded-lg bg-slate-50 p-2">
                  <p className="text-slate-400 flex items-center gap-1 text-[10px]"><Flag size={11} /> Reports</p>
                  <p className={`font-semibold ${trust.reports.open ? "text-red-600" : "text-[#12234A]"}`}>{trust.reports.total ? `${trust.reports.open} open · ${trust.reports.total}` : "Clean"}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
                <span className={`rounded-full px-2 py-0.5 ${trust.verified ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{trust.verified ? "Verified" : "Not verified"}</span>
                {trust.suspended && <span className="rounded-full px-2 py-0.5 bg-red-100 text-red-700">Suspended</span>}
                <span className={`rounded-full px-2 py-0.5 ${u.presence === "active" ? "bg-emerald-100 text-emerald-700" : u.presence === "away" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{u.presence === "active" ? "Online" : u.presence === "away" ? "Away" : "Offline"}</span>
              </div>
              <p data-testid={`user-peek-lastseen-${u.user_id}`} className="text-[11px] text-slate-400 flex items-center gap-1 pt-0.5">
                <Clock size={11} /> {u.presence === "active" ? "Active now" : u.presence === "away" ? "Away — idle" : u.last_seen ? `Last active ${timeAgo(u.last_seen)}` : "No recent activity"}
              </p>
            </>
          ) : (
            <p className="text-xs text-slate-400 py-1">{u.email}</p>
          )}
          {canAct && (
            <div className="flex gap-2 pt-1">
              <button onClick={() => onMessage?.(u)} data-testid={`user-peek-message-${u.user_id}`} className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] font-semibold rounded-lg py-1.5 text-[#F5551E] border border-[#F5551E]/40 hover:bg-[#FFF4F0] transition-colors">
                <MessageCircle size={12} /> Message
              </button>
              {status === "suspended" ? (
                <button onClick={() => onReactivate?.(u.user_id)} data-testid={`user-peek-reactivate-${u.user_id}`} className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] font-semibold rounded-lg py-1.5 text-emerald-600 border border-emerald-300 hover:bg-emerald-50 transition-colors">
                  <PlayCircle size={12} /> Reactivate
                </button>
              ) : (
                <button onClick={() => onSuspend?.(u.user_id)} data-testid={`user-peek-suspend-${u.user_id}`} className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] font-semibold rounded-lg py-1.5 text-amber-600 border border-amber-300 hover:bg-amber-50 transition-colors">
                  <PauseCircle size={12} /> Suspend
                </button>
              )}
            </div>
          )}
          {canAct && u.is_verified_seller && (
            <button onClick={() => onUnverify?.(u)} data-testid={`user-peek-unverify-${u.user_id}`} className="w-full inline-flex items-center justify-center gap-1 text-[11px] font-semibold rounded-lg py-1.5 text-amber-600 border border-amber-300 hover:bg-amber-50 transition-colors">
              <BadgeX size={12} /> Remove verification
            </button>
          )}
          <Link to={`/seller/${u.user_id}`} target="_blank" rel="noopener noreferrer" data-testid={`user-peek-open-${u.user_id}`} className="flex items-center justify-center gap-1.5 text-xs font-semibold text-[#F5551E] hover:underline pt-1 border-t border-slate-100 mt-1">
            Open full profile <ExternalLink size={12} />
          </Link>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
